import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'baseui/card';
import { Grid, Cell } from 'baseui/layout-grid';
import { Value } from 'baseui/select';
import { Button } from 'baseui/button';
import { HeadingSmall } from 'baseui/typography';
import { toaster } from 'baseui/toast';
import { useGet, usePost } from '@/hooks/apiHooks';
import { getName, getCode } from 'country-list'
import useJobStore from '@/store/jobs';
import { COUNTRIES } from 'baseui/phone-input';
import ResumeUpload from './resume-upload';
import FormControls, { FormData as FormDataFields } from './form-controls';
import { validateCandidateForm, buildSocialUrl, URL_PREFIXES } from './validation';
import { JobCard } from '../jobs/job';
import { NavigateBack } from '../navigation/navigate-back';
import { DeveloperType } from '@/network/definitions/job';

const ApplyCandidateForm: React.FC = () => {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [resume, setResume] = useState<File | null>(null);
  const [currency, setCurrency] = useState('INR');
  const [frequency, setFrequency] = useState('monthly');
  const [phoneCountry, setPhoneCountry] = React.useState(
    COUNTRIES.US
  );
  const [formData, setFormData] = useState<FormDataFields>({
    name: '',
    email: '',
    phone: '',
    countryId: [],
    city: '',
    developerType: [],
    salaryCurrency: currency,
    salaryFrequency: frequency,
    expectedSalary: '',
    yearsOfExperience: '',
    noticePeriod: '2',
    note: '',
    linkedinUrl: '',
    githubUrl: '',
    googleScholarUrl: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getJobById = useJobStore((state) => state.getJobById);
  const job = getJobById(Number(jobId));
  const { data: { data: countries = [] } = {} } = useGet<any>('agency-api/countries');
  const submitMutation = usePost<FormData, unknown>(`agency-api/job/${jobId}/apply-candidate`);
  const [engagementTypes, setEngagementTypes] = useState([
    { label: 'Full-time Employee', id: 'fte' },
    { label: 'Contractor', id: 'contractor' },
    { label: 'Sub-contractor', id: 'sub_contractor' }, // Leave as default in case of API failure
  ])

  useEffect(() => {
    if (job?.agencyType) {
      setEngagementTypes(job.agencyType === 'subcontractor' ?
        [{ label: 'Sub-contractor', id: DeveloperType.SUB_CONTRACTOR }]
        : [
          { label: 'Full-time Employee', id: DeveloperType.FTE },
          { label: 'Contractor', id: DeveloperType.CONTRACTOR }
        ])
    }
  }, [job])

  useEffect(() => {
    if (!formData.developerType.length) {
      if (job?.agencyType === 'subcontractor')
        setFormData({ ...formData, developerType: [{ id: DeveloperType.SUB_CONTRACTOR }] })
      else
        setFormData({ ...formData, developerType: [{ id: DeveloperType.CONTRACTOR }] });
    } else {
      handleRoleChange(String(formData.developerType[0].id));
    }
    updateCountryCurrency()
  }, [formData.developerType, formData.countryId]);

  const extractUsername = (pasted: string, name: keyof typeof URL_PREFIXES): string => {
    try {
      const parsed = new URL(pasted);
      if (name === 'linkedinUrl') {
        const match = parsed.pathname.match(/\/in\/([^/?#]+)/);
        return match ? match[1] : pasted;
      }
      if (name === 'githubUrl') {
        const parts = parsed.pathname.split('/').filter(Boolean);
        return parts[0] || pasted;
      }
      if (name === 'googleScholarUrl') {
        return parsed.searchParams.get('user') || pasted;
      }
    } catch {
      // not a URL — use as-is
    }
    return pasted;
  };

  const validateForm = (): boolean => {
    const phoneDigits = formData.phone
      .replace(phoneCountry.dialCode, '')
      .replace(/\D/g, '').length;
    const newErrors = validateCandidateForm({ formData, hasResume: !!resume, phoneDigits });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUrlBlur = (name: 'linkedinUrl' | 'githubUrl' | 'googleScholarUrl') => {
    setErrors((prev) => {
      const next = { ...prev };
      if (name === 'linkedinUrl' && !formData[name]) {
        next.linkedinUrl = 'LinkedIn username is required';
        return next;
      }
      const { error } = buildSocialUrl(name, formData[name] as string);
      if (error) next[name] = error;
      else delete next[name];
      return next;
    });
  };

  const handleUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: 'linkedinUrl' | 'githubUrl' | 'googleScholarUrl',
  ) => {
    let { value } = e.target;
    const looksLikeUrl =
      value.startsWith('http') ||
      value.startsWith('www.') ||
      value.includes('.com/') ||
      value.includes('.google.com/');
    if (looksLikeUrl) {
      const normalized = value.startsWith('http') ? value : `https://${value}`;
      value = extractUsername(normalized, name);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateCountryCurrency = () => {
    const countryData = formData.countryId;
    let defaultCurrency = String(formData?.developerType?.[0]?.id) === 'fte' ? 'INR' : 'USD'
    const currency = countryData?.[0]?.currency || defaultCurrency;
    // Only update currency for FTE developers, else leave as current
    if (currency && String(formData?.developerType?.[0]?.id) === 'fte') {
      setCurrency(currency);
      setFormData({ ...formData, salaryCurrency: currency, salaryFrequency: 'annually' });
    }
  }

  const handleSelectChange = (name: keyof FormData, value: Value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleRoleChange = (role: string) => {
    let currencyToSelect = currency, frequencyToSelect = frequency;
    if (role !== 'fte') {
      currencyToSelect = 'USD';
      frequencyToSelect = 'hourly';
      setCurrency(currencyToSelect);
    } else {
      frequencyToSelect = 'annually';
      updateCountryCurrency();
    }
    setFrequency(frequencyToSelect);
    setFormData({ ...formData, salaryCurrency: currencyToSelect, salaryFrequency: frequencyToSelect });
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const formPayload = new FormData();

      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof FormData];
        if (key === 'countryId' || key === 'developerType') {
          formPayload.append(key, (value[0] as any).id);
        } else if (key === 'linkedinUrl' || key === 'githubUrl' || key === 'googleScholarUrl') {
          const { url } = buildSocialUrl(key, value as string);
          if (url) formPayload.append(key, url);
        } else {
          formPayload.append(key, String(value));
        }
      });
      if (resume) {
        formPayload.append('resume', resume);
      }

      submitMutation.mutate(formPayload, {
        onSuccess: (res: any) => {
          if (res && res.statusCode && ![200, 201].includes(res.statusCode)) {
            if (res.message.message) {
              if (Array.isArray(res.message.message)) {
                toaster.negative(res.message.message[0], {});
                return;
              } else {
                toaster.negative(res.message.message, {});
                return;
              }
            } else {
              toaster.negative(res.message, {});
              return;
            }
          }
          toaster.positive('Candidate Added Successfully!', {
            autoHideDuration: 3000
          },);
          navigate(`/job/${jobId}`);
        },
        onError: () => {
          toaster.negative('Failed to create candidate', {});
        },
      });
    }
  };

  let phoneInputCountries: any[] = [];
  const missingCountriesWithCode = {
    Bolivia: "BO",
    "Cape Verde": "CV",
    "Republic of the Congo": "CG",
    "Croatia (Hrvatska)": "HR",
    "Czech Republic": "CZ",
    "Heard and Mc Donald Islands": "HM",
    Iran: "IR",
    "Ivory Coast": "CI",
    "North Korea": "KP",
    "South Korea": "KR",
    "Libyan Arab Jamahiriya": "LY",
    Macau: "MO",
    Palestine: "PS",
    Reunion: "RE",
    "South Georgia South Sandwich Islands": "GS",
    "St. Helena": "SH",
    "St. Pierre and Miquelon": "PM",
    "Svalbard and Jan Mayen Islands": "SJ",
    Swaziland: "SZ",
    Taiwan: "TW",
    Turkey: "TR",
    "United Kingdom": "GB",
    "United States": "US",
    "Vatican City State": "VA",
    Venezuela: "VE",
    Vietnam: "VN",
    "Virgin Islands (British)": "VG",
    "Virgin Islands (U.S.)": "VI",
    "Wallis and Futuna Islands": "WF",
    "Democratic Republic of the Congo": "CD",
    "Saint Martin": "MF",
    "Sint Maarten": "SX"
  };
  if (Array.isArray(countries) && countries.length > 0) {
    phoneInputCountries = countries
      .map((country) => {
        let isoCode = getCode(country.name);

        if (!isoCode) {
          return missingCountriesWithCode[country.name]
        }

        const correctedName = isoCode ? getName(isoCode) : country.name;

        return getCode(correctedName) || null;
      })
  }

  if (!job) return null;

  return (
    <>
      <NavigateBack
        route={`/job/${jobId}`}
        text={`Go back to job`}
      />
      {job.active ? (
        <>
          <JobCard job={job} actionComponent={<span />} />
          <Card
            overrides={{
              Root: {
                style: ({ $theme }) => ({
                  backgroundColor: $theme.colors.backgroundSecondary, // Customize the color
                  padding: $theme.sizing.scale100,
                  minWidth: "100%",
                  width: "100%",
                }),
              },
            }}
          >
            <Grid>
              <Cell span={12}>
                <HeadingSmall>Add a candidate</HeadingSmall>
              </Cell>
              <ResumeUpload setResume={setResume} errors={errors?.resume || ""} />
              <FormControls
                formData={formData}
                countries={countries}
                phoneInputCountries={phoneInputCountries}
                engagementTypes={engagementTypes}
                phoneCountry={phoneCountry}
                // @ts-ignore
                setPhoneCountry={setPhoneCountry}
                errors={errors}
                handleChange={handleChange}
                handleUrlBlur={handleUrlBlur}
                handleUrlChange={handleUrlChange}
                // @ts-ignore
                handleSelectChange={handleSelectChange}
                currency={currency}
              />
              <Cell span={12}>
                <Button onClick={handleSubmit} isLoading={submitMutation.isPending}>Add candidate</Button>
              </Cell>
            </Grid>
          </Card>
        </>
      ) : (
        <Card
          overrides={{
            Root: {
              style: ({ $theme }) => ({
                backgroundColor: $theme.colors.backgroundSecondary, // Customize the color
                padding: $theme.sizing.scale100,
                minWidth: "100%",
                width: "100%",
              }),
            },
          }}
        >
          <span className="flex">Job is not accepting applications right now.</span>
        </Card>
      )}
    </>
  );
};

export default ApplyCandidateForm;